import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import logoEmpresa from '../images/logo-empresa.png';

const fetchCotizacionData = async (cotizacionId) => {
  const cotizaDoc = await getDoc(doc(db, 'SicCotizaEncabezado', cotizacionId));

  if (!cotizaDoc.exists()) {
    throw new Error('Cotización no encontrada');
  }

  const cotizaData = cotizaDoc.data();

  const detallesQuery = query(
    collection(db, 'SicCotizaDetalles'),
    where('numCotizacion', '==', cotizaData.numCotizacion)
  );

  const detallesSnapshot = await getDocs(detallesQuery);
  const detallesData = detallesSnapshot.docs.map(detalleDoc => ({
    id: detalleDoc.id,
    ...detalleDoc.data(),
  }));

  return {
    cotizacion: cotizaData,
    detalles: detallesData,
  };
};

const formatCurrencyValue = (cotizacion, amount) => {
  if (!cotizacion || amount === null || amount === undefined) return '₡0.00';

  const formatter = new Intl.NumberFormat(cotizacion.moneda === 'Dólares' ? 'en-US' : 'es-CR', {
    style: 'currency',
    currency: cotizacion.moneda === 'Dólares' ? 'USD' : 'CRC',
    minimumFractionDigits: 2,
  });

  return formatter.format(amount);
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'Fecha no disponible';

  try {
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      return 'Fecha no válida';
    }

    return date.toLocaleDateString('es-CR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formateando fecha:', error);
    return 'Fecha no válida';
  }
};

const generatePrintContent = (cotizacion, detalles) => {
  if (!cotizacion) return '';

  return `
      <div class="reporte-cotizacion">
        <!-- Encabezado -->
        <div class="report-header">
          <div class="header-titles">
            <div class="header-info">
              <h2><strong>Cotización No:</strong> ${cotizacion.numCotizacion}</h2>
              <p><strong>Fecha:</strong> ${formatDate(cotizacion.fecha)}</p>
             
            </div>
          </div>
          <div class="logo-container">
            <img src="${logoEmpresa}" alt="Logo de la empresa" class="logo" />
          </div>
        </div>

              <!-- Información del cliente - Versión con array -->
        <div class="info-cliente">
          <div class="info-grid-aligned">
            <div class="info-row">
              <span class="info-label"><strong>Cliente:</strong></span>
              <span class="info-value">${cotizacion.cliente || 'No especificado'}</span>
            </div>
            ${[
              { label: 'Atención:', value: cotizacion.atencion },
              { label: 'Correo:', value: cotizacion.correo },
              { label: 'Teléfono Oficina:', value: cotizacion.telefonoOficina },
              { label: 'Teléfono Celular:', value: cotizacion.telefonoCelular }
            ]
            .filter(field => field.value) // Filtra solo los campos con valor
            .map(field => `
              <div class="info-row">
                <span class="info-label"><strong>${field.label}</strong></span>
                <span class="info-value">${field.value}</span>
              </div>
            `).join('')}
            <div class="info-row">
              <span class="info-label"><strong>Moneda:</strong></span>
              <span class="info-value">${cotizacion.moneda || 'No especificada'}</span>
            </div>
            <div class="info-row">
              <span class="info-label"><strong>Forma de Pago:</strong></span>
              <span class="info-value">${cotizacion.formaDePago || 'No especificado'}${cotizacion.formaDePago === 'Crédito' && cotizacion.plazo ? ` (${cotizacion.plazo} días)` : ''}</span>
            </div>
          </div>
        </div>

        <!-- Tabla de detalles -->
        <table>
          <thead>
            <tr>
              <th style="width: 8%">Cant.</th>
              <th style="width: 62%">Descripción</th>
              <th style="width: 15%">Precio Unitario</th>
              <th style="width: 15%">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${(detalles || []).map(detalle => `
              <tr>
                <td class="align-center">${detalle.cantidad || 0}</td>
                <td>${detalle.detalle || ''}</td>
                <td class="align-right">${formatCurrencyValue(cotizacion, detalle.subtotal / (detalle.cantidad || 1))}</td>
                <td class="align-right">${formatCurrencyValue(cotizacion, detalle.subtotal)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

    <!-- Tabla de totales y observaciones -->
        <table style="margin-top: 20px;">
          <tbody>
            <tr>
              <td style="width: 70%; vertical-align: top; border: 1px solid #000; padding: 10px; background-color: #f9f9f9;">
                ${cotizacion.observaciones ? `
                  <h3 style="margin-top: 0; margin-bottom: 8px;">Observaciones:</h3>
                  <div style="margin: 0; white-space: pre-line;">${cotizacion.observaciones}</div>
                ` : ''}
                <p style="margin: ${cotizacion.observaciones ? '10px 0 0 0' : '0'};"><strong>Hecho por:</strong> ${cotizacion.hechopor || 'No especificado'}</p>
              </td>
              <td style="width: 30%; vertical-align: top; border: 1px solid #000; padding: 10px; background-color: #f9f9f9;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                  <span>Subtotal:</span>
                  <span>${formatCurrencyValue(cotizacion, cotizacion.subtotal)}</span>
                </div>
                ${!cotizacion.exento && cotizacion.impuesto > 0 ? `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                    <span>IVA (${cotizacion.iva}%):</span>
                    <span>${formatCurrencyValue(cotizacion, cotizacion.impuesto)}</span>
                  </div>
                ` : ''}
                <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-weight: bold;">
                  <span>Total:</span>
                  <span>${formatCurrencyValue(cotizacion, cotizacion.total)}</span>
                </div>
                ${cotizacion.exento ? `
                  <div style="font-style: italic; color: #666; font-size: 10px; margin-top: 5px;">
                    * Esta cotización está exenta de IVA
                  </div>
                ` : ''}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="separator"></div>
        <div class="footer">
          <p>Especialistas en Desarrollo de Sistemas, Asesorías, Venta y Reparación de Equipo de Cómputo</p>
          <p>Cédula Jurídica 3-101-145050. San Vicente, Moravia. San José. Costa Rica.</p>
          <p>Teléfono: (506) 2240-9561 / Email: info@solinme.com</p>
          <p style="margin-top: 5px;">¡Gracias por su preferencia!</p>
        </div>
      </div>
  `;
};

const buildPrintHtml = (cotizacion, detalles) => `
  <!DOCTYPE html>
  <html>
    <head>
      <title>PROFORMA #${cotizacion?.numCotizacion || ''}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 15px;
          font-size: 12px;
          color: #000;
        }
        .report-header {
          margin-bottom: 20px;
          margin-top: 5px;
          border-bottom: 2px solid #ddd;
          padding-bottom: 15px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        }
        .header-titles {
          flex-grow: 1;
          text-align: left;
        }
        .logo-container {
          margin-left: 20px;
          text-align: right;
        }
        .logo {
          max-width: 120px;
          max-height: 120px;
        }
        .header-info {
          margin-top: 10px;
          margin-bottom: 5px;
          font-size: 12px;
          text-align: left;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 11px;
        }
        th, td {
          border: 1px solid #000;
          padding: 6px;
          text-align: left;
        }
        th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        .align-right {
          text-align: right;
        }
        .align-center {
          text-align: center;
        }
        .detalles-table {
          width: 100%;
          margin: 10px 0;
        }
        .info-cliente {
          background-color: #f9f9f9;
          padding: 6px;
          border: 1px solid #000;
          margin: 8px 0;
        }
        .info-grid-aligned {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .info-row {
          display: flex;
          align-items: center;
        }
        .info-label {
          width: 140px; /* Ancho fijo para las etiquetas */
          text-align: right;
          padding-right: 8px;
          font-size: 11px;
        }
        .info-value {
          flex: 1;
          font-size: 11px;
        }
        .separator {
          border-top: 2px solid #000;
          margin: 10px 0;
        }
        .footer {
          text-align: center;
          margin-top: 25px;
          font-size: 10px;
          color: #666;
        }
        
        /* ESTILOS PARA IMPRESIÓN - CORREGIDOS */
        @media screen {
          body {
            display: none;
          }
        }
        @media print {
          body {
            display: block !important;
          }
        }
        @page {
          margin: 0.5cm;
          size: letter;
        }
      </style>
    </head>
    <body>
      ${generatePrintContent(cotizacion, detalles)}
      <script>
        window.onload = function() {
          // Forzar la impresión inmediatamente
          window.print();
          
          // Cerrar después de un tiempo más largo para dar tiempo a la impresión
          setTimeout(() => {
            window.close();
          }, 500);
        };
      </script>
    </body>
  </html>
`;

export const abrirReporteCotizacion = async (cotizacionId) => {
  try {
    const { cotizacion, detalles } = await fetchCotizacionData(cotizacionId);
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      throw new Error('No se pudo abrir la ventana de impresión.');
    }

    printWindow.document.write(buildPrintHtml(cotizacion, detalles));
    printWindow.document.close();
    printWindow.focus();
  } catch (error) {
    console.error('Error generando el reporte de cotización:', error);
    alert(error.message || 'Error generando el reporte de cotización.');
  }
};

// Exportar solo lo necesario
export { fetchCotizacionData, formatCurrencyValue, formatDate };